import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiPackage,
  FiDownload,
  FiSettings,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiHardDrive,
  FiFileText,
  FiFolder,
  FiUpload,
  FiRefreshCw,
  FiServer,
  FiLink,
  FiSearch
} from 'react-icons/fi';

const initialPackageConfig = () => ({
  name: '',
  version: new Date().toISOString().slice(0, 10).replace(/-/g, '.'),
  description: '',
  includeTextures: true,
  includeModels: true,
  includeHistory: true,
  compressionLevel: 6,
  selectedFiles: []
});

const Packaging = ({
  files,
  packages,
  remotePackages,
  packageDirectoryState,
  remotePackageState,
  transferState,
  onOpenPackageDirectory,
  onRefreshLocalPackages,
  onCreatePackage,
  onDownloadLocalPackage,
  onDeleteLocalPackage,
  onConfigureRemote,
  onRefreshRemotePackages,
  onUploadPackage,
  onDownloadRemotePackage,
  onDeleteRemotePackage,
  packageApiService
}) => {
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [showRemoteConfig, setShowRemoteConfig] = useState(false);
  const [packageConfig, setPackageConfig] = useState(initialPackageConfig());
  const [isCreating, setIsCreating] = useState(false);
  const [isConfiguringRemote, setIsConfiguringRemote] = useState(false);
  const [endpointInput, setEndpointInput] = useState(remotePackageState?.endpoint || '');
  const [bearerTokenInput, setBearerTokenInput] = useState(remotePackageState?.bearerToken || '');
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [envConfig, setEnvConfig] = useState(null);
  const [fieldManagement, setFieldManagement] = useState({
    endpoint: { isManaged: false, fromEnvironment: false },
    token: { isManaged: false, fromEnvironment: false }
  });

  const localPackages = Array.isArray(packages) ? packages : [];
  const remoteInventory = useMemo(
    () => Array.isArray(remotePackages) ? remotePackages : [],
    [remotePackages]
  );

  const remotePackageNames = useMemo(
    () => new Set(remoteInventory.map(pkg => pkg.name)),
    [remoteInventory]
  );

  const filteredFiles = useMemo(() => {
    const query = fileSearchQuery.trim().toLowerCase();

    return files.filter(file => {
      if (!query) return true;
      return file.name.toLowerCase().includes(query) || file.path.toLowerCase().includes(query);
    });
  }, [files, fileSearchQuery]);

  const computedPreviewCount = useMemo(() => {
    const selected = new Set(packageConfig.selectedFiles || []);

    return files.filter(file => {
      if (!selected.has(file.path)) return false;

      const extension = (file.extension || '').toLowerCase();
      if (['.png', '.jpg', '.jpeg'].includes(extension) && !packageConfig.includeTextures) return false;
      if (['.glb', '.gltf'].includes(extension) && !packageConfig.includeModels) return false;
      if (file.name.toLowerCase().includes('history') && !packageConfig.includeHistory) return false;
      return true;
    }).length;
  }, [files, packageConfig]);

  const packageDirectoryReady = !!packageDirectoryState?.isAvailable;
  const remoteConfigured = !!remotePackageState?.isConfigured;
  const remoteConnected = !!remotePackageState?.isConnected;

  // Initialize environment configuration on component mount
  React.useEffect(() => {
    // Get environment configuration from PackageApiService
    if (packageApiService) {
      const envConfig = packageApiService.getEnvironmentConfig();
      const serviceStatus = packageApiService.getConnectionStatus();

      if (process.env.NODE_ENV === 'development') {
        console.info('[Packaging] Initialize remote env config', {
          envConfig,
          serviceStatus,
          incomingRemotePackageState: remotePackageState
        });
      }
      
      setEnvConfig({
        hasEndpoint: envConfig.hasEndpoint,
        hasToken: envConfig.hasToken,
        endpoint: envConfig.hasEndpoint ? packageApiService.endpoint : '',
        token: envConfig.hasToken ? packageApiService.bearerToken : '',
        isConfigured: envConfig.isConfigured
      });
      
      setFieldManagement({
        endpoint: {
          isManaged: envConfig.isEndpointManaged,
          fromEnvironment: envConfig.isEndpointManaged
        },
        token: {
          isManaged: envConfig.isTokenManaged,
          fromEnvironment: envConfig.isTokenManaged
        }
      });
      
      // Pre-populate inputs if environment variables are set and inputs are empty
      if (envConfig.hasEndpoint && !endpointInput) {
        setEndpointInput(packageApiService.endpoint);
      }
      if (envConfig.hasToken && !bearerTokenInput) {
        setBearerTokenInput(packageApiService.bearerToken);
      }
    }
  }, [packageApiService, endpointInput, bearerTokenInput, remotePackageState]);

  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development' || !packageApiService) return;

    const serviceStatus = packageApiService.getConnectionStatus();
    const envStatus = packageApiService.getEnvironmentConfig();

    console.info('[Packaging] Remote state snapshot', {
      remoteConfiguredFromAppState: !!remotePackageState?.isConfigured,
      remoteConnectedFromAppState: !!remotePackageState?.isConnected,
      remoteEndpointFromAppState: remotePackageState?.endpoint || '',
      serviceStatus,
      envStatus,
      remoteInventoryCount: Array.isArray(remotePackages) ? remotePackages.length : 0
    });
  }, [remotePackageState, remotePackages, packageApiService]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';

    const resolved = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(resolved.getTime())) return 'N/A';

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(resolved);
  };

  const handleCreatePackage = async () => {
    if (!packageDirectoryReady) {
      alert('Select a package directory before creating a package.');
      return;
    }

    if (!packageConfig.name || !packageConfig.version) {
      return;
    }

    if (!packageConfig.selectedFiles || packageConfig.selectedFiles.length === 0) {
      alert('Select at least one file to include in package.');
      return;
    }

    setIsCreating(true);

    try {
      const ok = await onCreatePackage(packageConfig);

      if (!ok) {
        return;
      }

      setShowCreatePackage(false);
      setPackageConfig(initialPackageConfig());
      setFileSearchQuery('');
    } catch (error) {
      console.error('Error creating package:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenCreateModal = () => {
    setPackageConfig({
      ...initialPackageConfig(),
      selectedFiles: files.map(file => file.path)
    });
    setFileSearchQuery('');
    setShowCreatePackage(true);
  };

  const toggleFileSelection = (filePath) => {
    setPackageConfig(prev => {
      const current = new Set(prev.selectedFiles || []);

      if (current.has(filePath)) {
        current.delete(filePath);
      } else {
        current.add(filePath);
      }

      return {
        ...prev,
        selectedFiles: Array.from(current)
      };
    });
  };

  const selectAllFilteredFiles = () => {
    const selected = new Set(packageConfig.selectedFiles || []);
    filteredFiles.forEach(file => selected.add(file.path));

    setPackageConfig(prev => ({
      ...prev,
      selectedFiles: Array.from(selected)
    }));
  };

  const clearAllFileSelection = () => {
    setPackageConfig(prev => ({
      ...prev,
      selectedFiles: []
    }));
  };

  const handleSaveRemoteConfig = async () => {
    setIsConfiguringRemote(true);

    try {
      const connected = await onConfigureRemote(endpointInput, bearerTokenInput);

      if (connected) {
        setShowRemoteConfig(false);
      }
    } finally {
      setIsConfiguringRemote(false);
    }
  };

  const handleDeleteLocalPackage = async (packageName) => {
    const confirmed = window.confirm(`Delete local package "${packageName}"?`);
    if (!confirmed) return;
    await onDeleteLocalPackage(packageName);
  };

  const handleDeleteRemotePackage = async (packageName) => {
    const confirmed = window.confirm(`Delete remote package "${packageName}" from cloud storage?`);
    if (!confirmed) return;
    await onDeleteRemotePackage(packageName);
  };

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-secondary-800 mb-2">Data Packaging</h2>
        <p className="text-secondary-600">
          Create ZIP packages in a local package directory and optionally sync with remote storage
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onOpenPackageDirectory}
          className="btn-outline flex items-center space-x-2"
        >
          <FiFolder />
          <span>{packageDirectoryReady ? 'Change Package Directory' : 'Select Package Directory'}</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onRefreshLocalPackages}
          className="btn-outline flex items-center space-x-2"
          disabled={!packageDirectoryReady}
        >
          <FiRefreshCw />
          <span>Refresh Local Packages</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleOpenCreateModal}
          className="btn-primary flex items-center space-x-2"
          disabled={!packageDirectoryReady || files.length === 0}
        >
          <FiPlus />
          <span>Create Package</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => setShowRemoteConfig(true)}
          className="btn-outline flex items-center space-x-2"
        >
          <FiSettings />
          <span>Remote Settings</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onRefreshRemotePackages}
          className="btn-outline flex items-center space-x-2"
          disabled={!remoteConfigured}
        >
          <FiServer />
          <span>Refresh Remote Packages</span>
        </motion.button>
      </div>

      {/* Status Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-secondary-500">Package Directory</p>
              <p className="font-semibold text-secondary-800 mt-1">
                {packageDirectoryReady
                  ? (packageDirectoryState.directoryPath || 'Selected directory')
                  : 'Not selected'}
              </p>
              <p className="text-xs text-secondary-500 mt-2">
                {packageDirectoryReady
                  ? `Last scanned: ${formatDate(packageDirectoryState.lastScanned)}`
                  : 'Select a directory to enable package create/delete operations'}
              </p>
            </div>
            {packageDirectoryReady ? (
              <FiCheckCircle className="w-5 h-5 text-success-600" />
            ) : (
              <FiXCircle className="w-5 h-5 text-error-600" />
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-secondary-500">Remote Storage</p>
              <p className="font-semibold text-secondary-800 mt-1">
                {remoteConfigured ? (remotePackageState.endpoint || 'Configured') : 'Not configured'}
              </p>
              <p className="text-xs text-secondary-500 mt-2">
                {remoteConfigured
                  ? (remoteConnected ? 'Connected' : (remotePackageState.error || 'Configured, not connected'))
                  : 'Configure endpoint and bearer token to enable upload/download'}
              </p>
            </div>
            {remoteConfigured && remoteConnected ? (
              <FiCheckCircle className="w-5 h-5 text-success-600" />
            ) : (
              <FiXCircle className="w-5 h-5 text-error-600" />
            )}
          </div>
        </div>
      </div>

      {/* Transfer State */}
      {transferState?.isTransferring && (
        <div className="bg-info-50 border border-info-200 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-info-800">
              {transferState.operation === 'upload' ? 'Uploading' :
                transferState.operation === 'download' ? 'Downloading' :
                transferState.operation === 'delete-remote' ? 'Deleting Remote Package' :
                transferState.operation === 'delete-local' ? 'Deleting Local Package' :
                'Transferring'}
              {transferState.currentFile ? `: ${transferState.currentFile}` : ''}
            </p>
            <p className="text-sm text-info-700">{transferState.progress || 0}%</p>
          </div>
          <div className="w-full h-2 rounded-full bg-info-100 overflow-hidden">
            <div
              className="h-full bg-info-500 transition-all duration-200"
              style={{ width: `${transferState.progress || 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Package Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Local Packages</p>
              <p className="text-2xl font-bold text-secondary-800">{localPackages.length}</p>
            </div>
            <FiPackage className="w-8 h-8 text-primary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Local Size</p>
              <p className="text-2xl font-bold text-secondary-800">
                {formatFileSize(localPackages.reduce((sum, pkg) => sum + (pkg.size || 0), 0))}
              </p>
            </div>
            <FiHardDrive className="w-8 h-8 text-secondary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Packaged Files</p>
              <p className="text-2xl font-bold text-secondary-800">
                {localPackages.reduce((sum, pkg) => sum + (pkg.fileCount || 0), 0)}
              </p>
            </div>
            <FiFileText className="w-8 h-8 text-accent-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Remote Packages</p>
              <p className="text-lg font-bold text-secondary-800">
                {remoteInventory.length}
              </p>
            </div>
            <FiServer className="w-8 h-8 text-warning-500" />
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Local Package List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-secondary-800">Local Package Directory</h3>
          {localPackages.map((pkg, index) => (
            <motion.div
              key={`local-${pkg.id || pkg.name}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-soft border border-secondary-100 p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-primary-50 rounded-lg">
                    <FiPackage className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">{pkg.name}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-secondary-500">
                      <span>v{pkg.version || 'unknown'}</span>
                      <span>•</span>
                      <span>{formatFileSize(pkg.size || 0)}</span>
                      <span>•</span>
                      <span>{pkg.fileCount || 0} files</span>
                      <span>•</span>
                      <span>{formatDate(pkg.created)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {remotePackageNames.has(pkg.name) ? (
                        <span className="text-xs bg-success-100 text-success-800 px-2 py-1 rounded">In Remote</span>
                      ) : (
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded">Local Only</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onDownloadLocalPackage(pkg.name)}
                    className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                    title="Download package file"
                  >
                    <FiDownload className="w-4 h-4 text-secondary-600" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onUploadPackage(pkg.name)}
                    className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                    disabled={!remoteConfigured || transferState?.isTransferring}
                    title="Upload to remote"
                  >
                    <FiUpload className="w-4 h-4 text-primary-600" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteLocalPackage(pkg.name)}
                    className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                    title="Delete local package"
                  >
                    <FiTrash2 className="w-4 h-4 text-error-600" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}

          {localPackages.length === 0 && (
            <div className="bg-white rounded-xl shadow-soft border border-secondary-100 p-6 text-center text-secondary-600">
              <FiPackage className="w-10 h-10 text-secondary-300 mx-auto mb-2" />
              No local packages found in selected package directory.
            </div>
          )}
        </div>

        {/* Remote Package List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-secondary-800">Remote Package Storage</h3>
          {remoteInventory.map((pkg, index) => (
            <motion.div
              key={`remote-${pkg.id || pkg.name}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-soft border border-secondary-100 p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-info-50 rounded-lg">
                    <FiServer className="w-5 h-5 text-info-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-800">{pkg.name}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-secondary-500">
                      <span>v{pkg.version || 'unknown'}</span>
                      <span>•</span>
                      <span>{formatFileSize(pkg.size || 0)}</span>
                      <span>•</span>
                      <span>{pkg.fileCount || 0} files</span>
                      <span>•</span>
                      <span>{formatDate(pkg.created)}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {localPackages.some(local => local.name === pkg.name) ? (
                        <span className="text-xs bg-success-100 text-success-800 px-2 py-1 rounded">Available Locally</span>
                      ) : (
                        <span className="text-xs bg-warning-100 text-warning-800 px-2 py-1 rounded">Remote Only</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onDownloadRemotePackage(pkg.name)}
                    className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                    disabled={!packageDirectoryReady || transferState?.isTransferring}
                    title="Download from remote to local package directory"
                  >
                    <FiDownload className="w-4 h-4 text-primary-600" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDeleteRemotePackage(pkg.name)}
                    className="p-2 rounded-lg hover:bg-secondary-100 transition-colors"
                    disabled={transferState?.isTransferring}
                    title="Delete remote package"
                  >
                    <FiTrash2 className="w-4 h-4 text-error-600" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}

          {remoteInventory.length === 0 && (
            <div className="bg-white rounded-xl shadow-soft border border-secondary-100 p-6 text-center text-secondary-600">
              <FiServer className="w-10 h-10 text-secondary-300 mx-auto mb-2" />
              {remoteConfigured
                ? 'No remote packages found. Upload a local package to start synchronization.'
                : 'Configure remote endpoint to list cloud packages.'}
            </div>
          )}
        </div>
      </div>

      {/* Create Package Modal */}
      <AnimatePresence>
        {showCreatePackage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="theme-modal rounded-xl shadow-strong p-6 w-full max-w-3xl max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Create New Package</h3>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Package Name
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="e.g., TK-11001B-Inspection"
                      value={packageConfig.name}
                      onChange={(e) => setPackageConfig({ ...packageConfig, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Version
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="e.g., 2026.02.21"
                      value={packageConfig.version}
                      onChange={(e) => setPackageConfig({ ...packageConfig, version: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Description
                    </label>
                    <textarea
                      className="input-field"
                      rows={3}
                      placeholder="Package description..."
                      value={packageConfig.description}
                      onChange={(e) => setPackageConfig({ ...packageConfig, description: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Include Categories
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={packageConfig.includeTextures}
                          onChange={(e) => setPackageConfig({ ...packageConfig, includeTextures: e.target.checked })}
                          className="rounded text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">Texture Files (.png, .jpg, .jpeg)</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={packageConfig.includeModels}
                          onChange={(e) => setPackageConfig({ ...packageConfig, includeModels: e.target.checked })}
                          className="rounded text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">3D Model Files (.glb, .gltf)</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={packageConfig.includeHistory}
                          onChange={(e) => setPackageConfig({ ...packageConfig, includeHistory: e.target.checked })}
                          className="rounded text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-secondary-700">History Metadata (name contains "history")</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Compression Level
                    </label>
                    <select
                      className="input-field"
                      value={packageConfig.compressionLevel}
                      onChange={(e) => setPackageConfig({ ...packageConfig, compressionLevel: parseInt(e.target.value, 10) })}
                    >
                      <option value={0}>No Compression</option>
                      <option value={1}>Fast</option>
                      <option value={6}>Balanced</option>
                      <option value={9}>Maximum</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-secondary-700">
                    File-Level Selection
                  </label>

                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                      <input
                        type="text"
                        className="input-field pl-9"
                        placeholder="Search files by name or path"
                        value={fileSearchQuery}
                        onChange={(e) => setFileSearchQuery(e.target.value)}
                      />
                    </div>
                    <button onClick={selectAllFilteredFiles} className="btn-outline text-sm">Select</button>
                    <button onClick={clearAllFileSelection} className="btn-outline text-sm">Clear</button>
                  </div>

                  <div className="border border-secondary-200 rounded-lg max-h-72 overflow-auto p-2 space-y-1">
                    {filteredFiles.map((file) => {
                      const checked = (packageConfig.selectedFiles || []).includes(file.path);

                      return (
                        <label key={file.path} className="flex items-start gap-2 p-2 rounded hover:bg-secondary-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleFileSelection(file.path)}
                            className="mt-1 rounded text-primary-600 focus:ring-primary-500"
                          />
                          <div>
                            <p className="text-sm text-secondary-800">{file.name}</p>
                            <p className="text-xs text-secondary-500">{file.path}</p>
                          </div>
                        </label>
                      );
                    })}

                    {filteredFiles.length === 0 && (
                      <div className="text-center text-sm text-secondary-500 py-6">
                        No files match current search.
                      </div>
                    )}
                  </div>

                  <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-3 text-sm text-secondary-700">
                    Selected files: <strong>{(packageConfig.selectedFiles || []).length}</strong><br />
                    Estimated included after category filters: <strong>{computedPreviewCount}</strong>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-6">
                <button
                  onClick={() => setShowCreatePackage(false)}
                  className="btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePackage}
                  disabled={isCreating || !packageConfig.name || !packageConfig.version || (packageConfig.selectedFiles || []).length === 0}
                  className={`btn-primary ${isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isCreating ? 'Creating...' : 'Create Package'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remote Configuration Modal */}
      <AnimatePresence>
        {showRemoteConfig && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50"
            onClick={() => setShowRemoteConfig(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="theme-modal rounded-xl shadow-strong p-6 w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
                <FiLink className="text-primary-600" />
                Remote Package API Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1 flex items-center gap-2">
                    Endpoint
                    {fieldManagement.endpoint.isManaged && (
                      <span className="text-xs bg-warning-100 text-warning-800 px-2 py-1 rounded flex items-center gap-1">
                        <FiSettings className="w-3 h-3" />
                        Managed by Environment
                      </span>
                    )}
                  </label>
                  <input
                    type="url"
                    className={`input-field ${fieldManagement.endpoint.isManaged ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                    placeholder="https://api.example.com"
                    value={endpointInput}
                    onChange={(e) => setEndpointInput(e.target.value)}
                    disabled={fieldManagement.endpoint.isManaged}
                    title={fieldManagement.endpoint.isManaged ?
                      'This field is managed by environment variables (REMOTE_PACKAGE_API_ENDPOINT)' :
                      'Enter the API endpoint URL'}
                  />
                  {fieldManagement.endpoint.isManaged && (
                    <p className="text-xs text-secondary-500 mt-1">
                      This value is set via the REMOTE_PACKAGE_API_ENDPOINT environment variable
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1 flex items-center gap-2">
                    Bearer Token
                    {fieldManagement.token.isManaged && (
                      <span className="text-xs bg-warning-100 text-warning-800 px-2 py-1 rounded flex items-center gap-1">
                        <FiSettings className="w-3 h-3" />
                        Managed by Environment
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    className={`input-field ${fieldManagement.token.isManaged ? 'bg-secondary-50 cursor-not-allowed' : ''}`}
                    placeholder="Paste bearer token"
                    value={bearerTokenInput}
                    onChange={(e) => setBearerTokenInput(e.target.value)}
                    disabled={fieldManagement.token.isManaged}
                    title={fieldManagement.token.isManaged ?
                      'This field is managed by environment variables (REMOTE_PACKAGE_API_TOKEN)' :
                      'Enter the bearer token for authentication'}
                  />
                  {fieldManagement.token.isManaged && (
                    <p className="text-xs text-secondary-500 mt-1">
                      This value is set via the REMOTE_PACKAGE_API_TOKEN environment variable
                    </p>
                  )}
                </div>

                {envConfig?.hasEndpoint && !envConfig?.hasToken && (
                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
                    <p className="font-medium mb-1">Partial Environment Configuration</p>
                    <p>REMOTE_PACKAGE_API_ENDPOINT is set but REMOTE_PACKAGE_API_TOKEN is missing. Please provide the token to complete the configuration.</p>
                  </div>
                )}

                {!envConfig?.hasEndpoint && envConfig?.hasToken && (
                  <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-700">
                    <p className="font-medium mb-1">Partial Environment Configuration</p>
                    <p>REMOTE_PACKAGE_API_TOKEN is set but REMOTE_PACKAGE_API_ENDPOINT is missing. Please provide the endpoint to complete the configuration.</p>
                  </div>
                )}

                {envConfig?.hasEndpoint && envConfig?.hasToken && (
                  <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-700">
                    <p className="font-medium mb-1">Fully Configured via Environment</p>
                    <p>Both REMOTE_PACKAGE_API_ENDPOINT and REMOTE_PACKAGE_API_TOKEN are set and managed externally.</p>
                  </div>
                )}

                {remotePackageState?.error && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">
                    {remotePackageState.error}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => setShowRemoteConfig(false)} className="btn-outline">
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveRemoteConfig}
                    disabled={isConfiguringRemote || !endpointInput || !bearerTokenInput ||
                            (fieldManagement.endpoint.isManaged && fieldManagement.token.isManaged)}
                    className={`btn-primary ${isConfiguringRemote ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isConfiguringRemote ? 'Connecting...' :
                     (fieldManagement.endpoint.isManaged && fieldManagement.token.isManaged) ?
                     'Configuration Managed by Environment' : 'Save & Connect'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Packaging;
